import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaTimes, FaUsers } from 'react-icons/fa';
import { apiService } from '../../components/Config/api.service';
import { ListeEquipe, Organigramme, jourMois } from '../../components/Equipe/VueEquipe';
import DemandesEquipe from './DemandesEquipe';
import HeuresSupPointage from '../Leave/HeuresSupPointage';
import FicheCollaborateur from '../Annuaire/FicheCollaborateur';

/**
 * « Mon équipe » (Yros/Kavero#9, #65, #286, contrat C32 ; fusionnée avec
 * l'organigramme par Yros/Kavero#488).
 *
 * Ouverte à tout salarié : son responsable (N+1) au-dessus de lui. Un manager
 * a en plus deux onglets, ses collaborateurs directs (N-1) et les demandes
 * qu'ils attendent de lui. Être manager, c'est avoir des N-1
 * (`_yros_rh_equipe()`) : un salarié sans équipe n'a rien d'autre à voir.
 *
 * L'équipe se lit avec `components/Equipe/VueEquipe` (lignes compactes,
 * filtres, recherche), partagé avec la fiche RH. Toute personne
 * s'ouvre dans la fiche de l'annuaire ; celle d'un N-1 y ajoute ce que le
 * manager doit savoir (congé, solde, demandes en attente).
 */

/**
 * « Pour vous, manager » dans la fiche d'un N-1 : ce que l'annuaire ne montre
 * à personne d'autre.
 */
function SectionManager({ equipier, nbDemandes, onVoirDemandes }) {
  const { t, i18n } = useTranslation();
  return (
    <section data-testid="fiche-section-manager" className="rounded-xl bg-slate-50 border border-slate-100 p-3 space-y-2">
      <h3 className="text-xs font-medium uppercase tracking-wide text-slate-400">{t('monEquipe.pourVous')}</h3>
      {equipier.conge_jusquau && (
        <p className="text-sm text-sky-700">{t('monEquipe.enConge', { date: jourMois(equipier.conge_jusquau, i18n) })}</p>
      )}
      {(equipier.soldes || []).map((solde) => (
        <p key={solde.libelle} className="text-sm text-slate-700">
          {t('monEquipe.solde', { libelle: solde.libelle, n: solde.restant })}
        </p>
      ))}
      {nbDemandes > 0 ? (
        <button type="button" data-testid="fiche-voir-demandes" onClick={onVoirDemandes}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-[--color-primary] text-white text-sm font-medium hover:bg-[--bg-hover] transition-colors">
          {t('monEquipe.voirSesDemandes', { count: nbDemandes })}
        </button>
      ) : (
        <p className="text-sm text-slate-500">{t('monEquipe.aucuneDemandeDe')}</p>
      )}
    </section>
  );
}

function Onglet({ id, actif, onClick, children }) {
  return (
    <button
      type="button"
      role="tab"
      id={`onglet-${id}`}
      data-testid={`onglet-${id}`}
      aria-selected={actif}
      aria-controls={`panneau-${id}`}
      onClick={onClick}
      className={`flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
        actif ? 'bg-white text-[--color-primary] shadow-sm' : 'text-slate-500 hover:text-slate-700'
      }`}
    >
      {children}
    </button>
  );
}

// Yros/Kavero#491 : le validateur des heures sup du pointage décide ici, dans
// « Demandes » s'il est manager, sinon dans un bloc à lui. Ses heures sup
// s'ajoutent au compteur des demandes.
function MonEquipe({ manager = false, validateur = false, onCompteDemandes }) {
  const { t } = useTranslation();
  const [moi, setMoi] = useState(null);
  const [equipe, setEquipe] = useState(null);
  const [erreur, setErreur] = useState(false);
  const [nbDemandes, setNbDemandes] = useState(null);
  const [nbHeuresSup, setNbHeuresSup] = useState(validateur ? null : 0);
  const [onglet, setOnglet] = useState('equipe');
  const [ongletChoisi, setOngletChoisi] = useState(false);
  const [demandes, setDemandes] = useState([]);
  const [ouvert, setOuvert] = useState(null);
  // Les demandes d'un seul N-1, ouvertes depuis sa fiche.
  const [demandeur, setDemandeur] = useState(null);

  useEffect(() => {
    let actif = true;
    apiService.getElement('/rh/moi')
      .then((reponse) => { if (actif && reponse?.data?.ok) setMoi(reponse.data.donnees); })
      .catch(() => {});
    if (manager) {
      apiService.getElement('/rh/mon-equipe')
        .then((reponse) => {
          if (!actif) return;
          if (reponse?.data?.ok) setEquipe(reponse.data.equipe || []);
          else setErreur(true);
        })
        .catch(() => { if (actif) setErreur(true); });
    }
    return () => { actif = false; };
  }, [manager]);

  // Le total part vers l'espace dès que chaque source a répondu : les demandes
  // de l'équipe pour un manager, les heures sup pour un validateur.
  const comptes = useRef({ equipe: manager ? null : 0, heuresSup: validateur ? null : 0 });
  const signalerTotal = useRef(onCompteDemandes);
  signalerTotal.current = onCompteDemandes;
  const compter = (cle, n) => {
    comptes.current[cle] = n;
    const { equipe: e, heuresSup: h } = comptes.current;
    if (e !== null && h !== null && signalerTotal.current) signalerTotal.current(e + h);
  };

  const compterDemandes = (n, liste) => {
    setDemandes(liste || []);
    setNbDemandes(n);
    compter('equipe', n);
  };
  const compterHeuresSup = useCallback((n) => {
    setNbHeuresSup(n);
    compter('heuresSup', n);
  }, []);

  const total = (manager ? nbDemandes : 0) === null || nbHeuresSup === null
    ? null : (manager ? nbDemandes : 0) + nbHeuresSup;
  // Des demandes attendent au premier décompte : la page s'ouvre sur elles,
  // tant que le manager n'a pas choisi d'onglet lui-même. Décider la dernière
  // ne le renvoie pas sur l'équipe.
  const premierTotal = useRef(null);
  if (premierTotal.current === null && total !== null) premierTotal.current = total;
  const ongletAffiche = ongletChoisi ? onglet : (premierTotal.current > 0 ? 'demandes' : 'equipe');

  const choisir = (id) => {
    setOngletChoisi(true);
    setOnglet(id);
  };

  const equipierOuvert = equipe?.find((e) => e.id === ouvert);
  const demandesDe = (id) => demandes.filter((d) => d.demandeur?.id === id).length;
  const voirDemandesDe = (equipier) => {
    setDemandeur(equipier);
    choisir('demandes');
    setOuvert(null);
  };

  return (
    <section className="mt-4 sm:mt-6 space-y-4">
      <h2 className="flex items-center gap-2 text-lg sm:text-xl font-bold text-slate-800">
        <FaUsers className="text-[--color-primary]" aria-hidden="true" />
        {t('monEquipe.titre')}
      </h2>

      <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100">
        {moi ? <Organigramme responsable={moi.responsable} moi={moi} onOuvrir={setOuvert} /> : (
          <p className="text-sm text-slate-500">{t('monEquipe.chargement')}</p>
        )}
      </div>

      {manager && (
        <>
          <div role="tablist" aria-label={t('monEquipe.titre')} className="flex gap-1 p-1 bg-slate-100 rounded-xl sm:w-fit">
            <Onglet id="equipe" actif={ongletAffiche === 'equipe'} onClick={() => choisir('equipe')}>
              {t('monEquipe.ongletEquipe')}
              {equipe && <span className="text-xs text-slate-400">{equipe.length}</span>}
            </Onglet>
            <Onglet id="demandes" actif={ongletAffiche === 'demandes'} onClick={() => choisir('demandes')}>
              {t('monEquipe.ongletDemandes')}
              {total > 0 && (
                <span data-testid="onglet-demandes-compteur"
                  className="min-w-[20px] px-1.5 py-0.5 rounded-full bg-red-600 text-white text-[11px] leading-none">
                  {total}
                </span>
              )}
            </Onglet>
          </div>

          <div id="panneau-equipe" role="tabpanel" aria-labelledby="onglet-equipe" hidden={ongletAffiche !== 'equipe'}
            className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100">
            {erreur && (
              <p role="alert" className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                {t('monEquipe.erreur')}
              </p>
            )}
            {!erreur && equipe === null && (
              <p className="text-sm text-slate-500">{t('monEquipe.chargement')}</p>
            )}
            {equipe && equipe.length === 0 && (
              <p className="text-sm text-slate-500">{t('monEquipe.vide')}</p>
            )}
            {equipe && equipe.length > 0 && <ListeEquipe equipe={equipe} onOuvrir={setOuvert} />}
          </div>

          {/* Toujours monté : le compteur se lit au chargement, quel que soit l'onglet. */}
          <div id="panneau-demandes" role="tabpanel" aria-labelledby="onglet-demandes" hidden={ongletAffiche !== 'demandes'}>
            {demandeur && (
              <p className="mb-3 flex items-center gap-2 text-sm text-slate-600">
                <span data-testid="demandes-filtre-demandeur"
                  className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[--color-primary] text-white text-xs font-medium">
                  {t('monEquipe.demandesDe', { nom: demandeur.nom })}
                  <button type="button" onClick={() => setDemandeur(null)} aria-label={t('monEquipe.toutesLesDemandes')}>
                    <FaTimes className="w-3 h-3" aria-hidden="true" />
                  </button>
                </span>
              </p>
            )}
            <DemandesEquipe onCompte={compterDemandes} demandeurId={demandeur?.id ?? null} />
            {validateur && (
              <div className="mt-4"><HeuresSupPointage onCompte={compterHeuresSup} masquerSiVide /></div>
            )}
            {total === 0 && (
              <p data-testid="equipe-demandes-vide"
                className="rounded-2xl bg-white border border-slate-100 p-4 text-sm text-slate-500">
                {t('monEquipe.aucuneDemande')}
              </p>
            )}
          </div>
        </>
      )}

      {!manager && validateur && <HeuresSupPointage onCompte={compterHeuresSup} />}

      {ouvert !== null && (
        <FicheCollaborateur
          employeId={ouvert}
          onOuvrir={setOuvert}
          onFermer={() => setOuvert(null)}
          complement={equipierOuvert ? (
            <SectionManager
              equipier={equipierOuvert}
              nbDemandes={demandesDe(equipierOuvert.id)}
              onVoirDemandes={() => voirDemandesDe(equipierOuvert)}
            />
          ) : null}
        />
      )}
    </section>
  );
}

export default MonEquipe;
